<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Draggable from 'vuedraggable';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const uiFlags = useMapGetter('inboxes/getUIFlags');

const enabled = ref(false);
const prompt = ref('');
const handoffMessage = ref('');
const menuItems = ref([]);
let rowId = 0;

const defaultPrompt = 'Выберите тему обращения';
const defaultHandoffMessage =
  'Ваш запрос передан менеджеру. Мы ответим вам в этом чате в ближайшее время.';
const MAX_MENU_ITEMS = 20;

const createRow = (title = '') => {
  rowId += 1;
  return { id: rowId, title };
};

const itemsPayload = computed(() =>
  menuItems.value
    .map(row => row.title.trim())
    .filter(Boolean)
    .map(title => ({ title, value: title }))
);

const serviceMenuOptions = computed(
  () =>
    props.inbox.service_menu_options ||
    props.inbox.channel?.service_menu_options ||
    {}
);

const setDefaults = () => {
  const options = serviceMenuOptions.value;
  enabled.value = !!options.enabled;
  prompt.value = options.prompt || defaultPrompt;
  handoffMessage.value = options.handoff_message || defaultHandoffMessage;
  const items = Array.isArray(options.items) ? options.items : [];
  const titles = items.map(item => item.title || item.value).filter(Boolean);
  rowId = 0;
  menuItems.value =
    titles.length > 0 ? titles.map(title => createRow(title)) : [createRow()];
};

watch(() => props.inbox, setDefaults, { deep: true });

onMounted(setDefaults);

const addMenuItem = () => {
  if (menuItems.value.length >= MAX_MENU_ITEMS) return;
  menuItems.value.push(createRow());
};

const removeMenuItem = index => {
  if (menuItems.value.length === 1) {
    menuItems.value[0].title = '';
    return;
  }
  menuItems.value.splice(index, 1);
};

const updateInbox = async () => {
  if (enabled.value && itemsPayload.value.length === 0) {
    useAlert(t('INBOX_MGMT.SERVICE_MENU.ITEMS_REQUIRED'));
    return;
  }

  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      channel: {
        service_menu_options: {
          enabled: enabled.value,
          prompt: prompt.value || defaultPrompt,
          handoff_message: handoffMessage.value || defaultHandoffMessage,
          items: itemsPayload.value,
        },
      },
    });
    useAlert(t('INBOX_MGMT.SERVICE_MENU.API.SUCCESS'));
    await nextTick();
    setDefaults();
  } catch {
    useAlert(t('INBOX_MGMT.SERVICE_MENU.API.ERROR'));
  }
};
</script>

<template>
  <div class="mx-8 max-w-4xl">
    <SettingsToggleSection
      v-model="enabled"
      :title="$t('INBOX_MGMT.SERVICE_MENU.ENABLE.TITLE')"
      :description="$t('INBOX_MGMT.SERVICE_MENU.ENABLE.DESCRIPTION')"
    />
    <div class="flex flex-col gap-4 mt-6">
      <label class="block">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.SERVICE_MENU.PROMPT.LABEL') }}
        </span>
        <input
          v-model="prompt"
          type="text"
          class="mt-1 w-full rounded-lg border border-n-weak px-3 py-2 text-sm"
          :placeholder="$t('INBOX_MGMT.SERVICE_MENU.PROMPT.PLACEHOLDER')"
        />
      </label>
      <label class="block">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.SERVICE_MENU.HANDOFF.LABEL') }}
        </span>
        <span class="block text-xs text-n-slate-11 mt-1 mb-1">
          {{ $t('INBOX_MGMT.SERVICE_MENU.HANDOFF.HELP') }}
        </span>
        <input
          v-model="handoffMessage"
          type="text"
          class="mt-1 w-full rounded-lg border border-n-weak px-3 py-2 text-sm"
          :placeholder="$t('INBOX_MGMT.SERVICE_MENU.HANDOFF.PLACEHOLDER')"
        />
      </label>
      <div class="block">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.SERVICE_MENU.ITEMS.LABEL') }}
        </span>
        <span class="block text-xs text-n-slate-11 mt-1 mb-3">
          {{ $t('INBOX_MGMT.SERVICE_MENU.ITEMS.HELP') }}
        </span>
        <Draggable
          v-model="menuItems"
          item-key="id"
          handle=".drag-handle"
          class="flex flex-col gap-2"
        >
          <template #item="{ element, index }">
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="drag-handle flex shrink-0 items-center justify-center p-1 text-n-slate-11 hover:text-n-slate-12"
                :aria-label="$t('INBOX_MGMT.SERVICE_MENU.ITEMS.DRAG')"
              >
                <Icon icon="i-woot-drag-indicator" class="size-4" />
              </button>
              <input
                v-model="element.title"
                type="text"
                class="min-w-0 flex-1 rounded-lg border border-n-weak px-3 py-2 text-sm"
                :placeholder="
                  $t('INBOX_MGMT.SERVICE_MENU.ITEMS.ROW_PLACEHOLDER', {
                    n: index + 1,
                  })
                "
              />
              <button
                type="button"
                class="flex shrink-0 items-center justify-center p-2 text-n-slate-11 hover:text-n-ruby-10"
                :aria-label="$t('INBOX_MGMT.SERVICE_MENU.ITEMS.REMOVE')"
                @click="removeMenuItem(index)"
              >
                <Icon icon="i-lucide-trash-2" class="size-4" />
              </button>
            </div>
          </template>
        </Draggable>
        <Button
          class="mt-3"
          variant="faded"
          color="blue"
          :label="$t('INBOX_MGMT.SERVICE_MENU.ITEMS.ADD')"
          icon="i-lucide-plus"
          :disabled="menuItems.length >= MAX_MENU_ITEMS"
          @click="addMenuItem"
        />
      </div>
    </div>
    <div class="flex justify-end mt-6">
      <Button
        :label="$t('INBOX_MGMT.SERVICE_MENU.UPDATE')"
        :is-loading="uiFlags.isUpdating"
        @click="updateInbox"
      />
    </div>
  </div>
</template>
